/**
 * Utility functions for transforming between dynamic team specification format
 * and filter picker format.
 *
 * Specification format example:
 * {
 *   "criterias": {
 *     "op": "intersection",
 *     "elements": [
 *       {
 *         "op": "union",
 *         "elements": [
 *           { "criteria": "anyone_in_workplace", "input": { "location_id": 1 } },
 *           { "criteria": "anyone_in_workplace", "input": { "location_id": 2 } }
 *         ]
 *       },
 *       {
 *         "op": "union",
 *         "elements": [
 *           { "criteria": "direct_reports_of", "input": { "employee_id": 3 } }
 *         ]
 *       }
 *     ]
 *   }
 * }
 *
 * Filter format example:
 * {
 *   location: [1, 2],
 *   direct_reports_of: [3]
 * }
 */

// Mapping between criteria names and their input key names
const CRITERIA_INPUT_MAPPING: Record<string, string> = {
  anyone_in_workplace: 'location_id',
  direct_reports_of: 'employee_id',
  anyone_in_team: 'team_id',
  anyone_in_legal_entity: 'legal_entity_id',
  all_reportees_of: 'employee_id',
  leads_in_team: 'team_id',
  manager_of: 'employee_id',
  timeoff_manager_of: 'employee_id',
}

// Mapping from filter keys to criteria names
const FILTER_TO_CRITERIA_MAPPING: Record<string, string> = {
  location: 'anyone_in_workplace',
  direct_reports_of: 'direct_reports_of',
  team: 'anyone_in_team',
  legal_entity: 'anyone_in_legal_entity',
  all_reportees_of: 'all_reportees_of',
  leads_in_team: 'leads_in_team',
  manager_of: 'manager_of',
  timeoff_manager_of: 'timeoff_manager_of',
}

// Mapping from criteria names to filter keys
const CRITERIA_TO_FILTER_MAPPING: Record<string, string> = {
  anyone_in_workplace: 'location',
  direct_reports_of: 'direct_reports_of',
  anyone_in_team: 'team',
  anyone_in_legal_entity: 'legal_entity',
  all_reportees_of: 'all_reportees_of',
  leads_in_team: 'leads_in_team',
  manager_of: 'manager_of',
  timeoff_manager_of: 'timeoff_manager_of',
}

export interface CriteriaNode {
  criteria: string
  input: Record<string, number>
}

export interface OperationNode {
  op: 'union' | 'intersection' | 'difference'
  elements: Array<CriteriaNode | OperationNode>
}

export interface Specification {
  criterias?: CriteriaNode | OperationNode
  whitelist?: number[]
  blacklist?: number[]
}

export type FilterState = Record<string, number[]>

export interface TransformationResult {
  success: boolean
  filters?: FilterState
  whitelist?: number[]
  blacklist?: number[]
  error?: string
}

/**
 * Check if a node is a criteria node (leaf node)
 */
function isCriteriaNode(node: CriteriaNode | OperationNode): node is CriteriaNode {
  return 'criteria' in node
}

/**
 * Check if a node is an operation node
 */
function isOperationNode(node: CriteriaNode | OperationNode): node is OperationNode {
  return 'op' in node
}

/**
 * Check if a specification's criterias can be transformed to filter format.
 *
 * Valid criterias:
 * 1. Single criteria
 * 2. Top-level intersection with union children (AND between entities, OR within an entity)
 * 3. Single union (OR between criteria of same type)
 * 4. Empty criterias (whitelist-only specs)
 *
 * Invalid criterias:
 * - Complex nested operations (intersection inside union, etc.)
 * - Mixed criteria types within the same union
 * - Difference operations
 *
 * Note: Whitelist and blacklist are allowed and will be preserved.
 */
export function canTransformToFilters(spec: Specification): boolean {
  // If no criterias, it's valid (whitelist-only spec or empty spec)
  if (!spec.criterias) {
    return true
  }

  const criterias = spec.criterias

  // Single criteria - always valid
  if (isCriteriaNode(criterias)) {
    return CRITERIA_TO_FILTER_MAPPING[criterias.criteria] !== undefined
  }

  // Must be an operation node
  if (!isOperationNode(criterias)) {
    return false
  }

  // Difference operation is not supported
  if (criterias.op === 'difference') {
    return false
  }

  // Single union - check all elements are criteria of known types
  if (criterias.op === 'union') {
    return criterias.elements.every(
      (el) => isCriteriaNode(el) && CRITERIA_TO_FILTER_MAPPING[el.criteria] !== undefined
    )
  }

  // Intersection at top level - each element should be union of same criteria type
  if (criterias.op === 'intersection') {
    return criterias.elements.every((el) => {
      // Direct criteria is valid
      if (isCriteriaNode(el)) {
        return CRITERIA_TO_FILTER_MAPPING[el.criteria] !== undefined
      }

      // Union of same criteria type is valid
      if (isOperationNode(el) && el.op === 'union') {
        const criteriaTypes = new Set<string>()
        for (const child of el.elements) {
          if (!isCriteriaNode(child)) {
            return false // Nested operations not allowed
          }
          if (CRITERIA_TO_FILTER_MAPPING[child.criteria] === undefined) {
            return false
          }
          criteriaTypes.add(child.criteria)
        }
        // All criteria in a union should be of the same type
        return criteriaTypes.size === 1
      }

      return false
    })
  }

  return false
}

/**
 * Transform a specification to filter format.
 *
 * Returns success: false if the criterias cannot be transformed.
 * Whitelist and blacklist are preserved and returned in the result.
 */
export function specificationToFilters(spec: Specification): TransformationResult {
  if (!canTransformToFilters(spec)) {
    return {
      success: false,
      error: 'SPECIFICATION_TRANSFORMATION_TO_FILTERS_ERROR',
    }
  }

  // Base result with preserved whitelist/blacklist
  const baseResult = {
    success: true,
    whitelist: spec.whitelist,
    blacklist: spec.blacklist,
  }

  if (!spec.criterias) {
    return { ...baseResult, filters: {} }
  }

  const filters: FilterState = {}
  const criterias = spec.criterias

  if (isCriteriaNode(criterias)) {
    const filterKey = CRITERIA_TO_FILTER_MAPPING[criterias.criteria]
    const inputKey = CRITERIA_INPUT_MAPPING[criterias.criteria]
    if (filterKey && inputKey) {
      const value = criterias.input[inputKey]
      if (value !== undefined) {
        filters[filterKey] = [value]
      }
    }
    return { ...baseResult, filters }
  }

  if (isOperationNode(criterias) && criterias.op === 'union') {
    const grouped: Record<string, number[]> = {}
    for (const el of criterias.elements) {
      if (isCriteriaNode(el)) {
        const filterKey = CRITERIA_TO_FILTER_MAPPING[el.criteria]
        const inputKey = CRITERIA_INPUT_MAPPING[el.criteria]
        if (filterKey && inputKey) {
          const value = el.input[inputKey]
          if (value !== undefined) {
            if (!grouped[filterKey]) {
              grouped[filterKey] = []
            }
            grouped[filterKey].push(value)
          }
        }
      }
    }
    return { ...baseResult, filters: grouped }
  }

  if (isOperationNode(criterias) && criterias.op === 'intersection') {
    for (const el of criterias.elements) {
      if (isCriteriaNode(el)) {
        const filterKey = CRITERIA_TO_FILTER_MAPPING[el.criteria]
        const inputKey = CRITERIA_INPUT_MAPPING[el.criteria]
        if (filterKey && inputKey) {
          const value = el.input[inputKey]
          if (value !== undefined) {
            if (!filters[filterKey]) {
              filters[filterKey] = []
            }
            filters[filterKey].push(value)
          }
        }
      } else if (isOperationNode(el) && el.op === 'union') {
        for (const child of el.elements) {
          if (isCriteriaNode(child)) {
            const filterKey = CRITERIA_TO_FILTER_MAPPING[child.criteria]
            const inputKey = CRITERIA_INPUT_MAPPING[child.criteria]
            if (filterKey && inputKey) {
              const value = child.input[inputKey]
              if (value !== undefined) {
                if (!filters[filterKey]) {
                  filters[filterKey] = []
                }
                filters[filterKey].push(value)
              }
            }
          }
        }
      }
    }
    return { ...baseResult, filters }
  }

  return { success: false, error: 'SPECIFICATION_TRANSFORMATION_TO_FILTERS_ERROR' }
}

export interface FiltersToSpecificationOptions {
  /** Whitelist to preserve in the specification */
  whitelist?: number[]
  /** Blacklist to preserve in the specification */
  blacklist?: number[]
}

/**
 * Transform filter state to specification format.
 *
 * Each filter group becomes a union of criteria, and groups are combined with intersection.
 * Optionally preserves whitelist/blacklist from the original specification.
 */
export function filtersToSpecification(
  filters: FilterState,
  options: FiltersToSpecificationOptions = {}
): Specification {
  const filterKeys = Object.keys(filters).filter(
    (key) => filters[key]?.length && filters[key].length > 0
  )
  const { whitelist, blacklist } = options

  const spec: Specification = {}
  if (whitelist?.length) {
    spec.whitelist = whitelist
  }
  if (blacklist?.length) {
    spec.blacklist = blacklist
  }

  if (filterKeys.length === 0) {
    return spec
  }

  const intersectionElements: Array<CriteriaNode | OperationNode> = []

  for (const filterKey of filterKeys) {
    const values = filters[filterKey]
    const criteriaName = FILTER_TO_CRITERIA_MAPPING[filterKey]
    const inputKey = criteriaName ? CRITERIA_INPUT_MAPPING[criteriaName] : undefined

    if (!criteriaName || !inputKey || !values?.length) {
      continue
    }

    const criteriaNodes: CriteriaNode[] = values.map((value) => ({
      criteria: criteriaName,
      input: { [inputKey]: value },
    }))

    if (criteriaNodes.length === 1 && criteriaNodes[0] !== undefined) {
      // Single value - add directly
      intersectionElements.push(criteriaNodes[0])
    } else {
      // Multiple values - wrap in union
      intersectionElements.push({
        op: 'union',
        elements: criteriaNodes,
      })
    }
  }

  if (intersectionElements.length === 0) {
    return spec
  }

  if (intersectionElements.length === 1) {
    spec.criterias = intersectionElements[0]
    return spec
  }

  // Multiple filter groups - combine with intersection
  spec.criterias = {
    op: 'intersection',
    elements: intersectionElements,
  }
  return spec
}

/**
 * Helper to safely get property from object
 */
function getObjectProperty(obj: Record<string, unknown>, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined
}

/**
 * Type guard to validate a parsed specification object
 */
function isValidSpecification(obj: Record<string, unknown>) {
  if (typeof obj !== 'object' || obj === null || obj === undefined) {
    return false
  }
  // Basic validation - the object should optionally have criterias (with blacklist or without it) or whitelist
  const criterias = getObjectProperty(obj, 'criterias')
  const whitelist = getObjectProperty(obj, 'whitelist')

  if (criterias !== undefined && typeof criterias !== 'object') {
    return false
  }
  if (whitelist !== undefined && !Array.isArray(whitelist)) {
    return false
  }
  return true
}

/**
 * Parse a JSON specification string to a Specification object
 */
export function parseSpecification(specificationJson: string): Specification | null {
  try {
    const parsed: Record<string, unknown> = JSON.parse(specificationJson)
    if (isValidSpecification(parsed)) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

/**
 * Stringify a Specification object to JSON
 */
export function stringifySpecification(spec: Specification): string {
  return JSON.stringify(spec)
}
